/**
 * The photo a taxonomy hero shows when the operator has not uploaded one.
 *
 * WHY THIS EXISTS. A destination or activity-type page with no featured image
 * used to paint a flat brand-tinted band — correct, honest, and visually dead
 * next to the tours index, which carries a photo. The ask (2026-10-26) was to
 * give those two surfaces a photo as well, chosen to suit the surface rather
 * than copying the tours index's forest aerial onto all three.
 *
 * WHAT THESE ARE, PLAINLY. Stock photography, identical on every tenant's
 * site, hotlinked from Unsplash. They are a visual floor, not the agency's own
 * work, and the operator replacing one with a real photo of the place or the
 * activity is always the better outcome — `heroImage` wins whenever it is set.
 * The URLs carry Unsplash's own transform parameters so the CDN returns a
 * hero-sized, compressed frame rather than the full original; `next/image`
 * then serves it at the width the viewport asks for.
 *
 * HOW TO JUDGE A REPLACEMENT. The band paints a heavy brand wash over the
 * photo (`heading` 35% → `primary` 85%,
 * `listing-banner-backdrop.module.css`) and sets the breadcrumb, title and
 * description on top of it, so a candidate is only judgeable WITH that
 * gradient over it — several frames that look right on their own go muddy
 * underneath. Two things matter:
 *
 * 1. THE LOWER LEFT HAS TO STAY QUIET. The copy sits there, over the deepest
 *    part of the wash. A bright or busy corner puts white text on near-white.
 *    Measured on the committed pair: title 6.0:1, breadcrumb 11.7–16.2:1.
 * 2. A WARM PATCH ANYWHERE GOES MUDDY. Orange or amber under a green wash
 *    turns brown — that is what retired the previous destination frame, and
 *    it ruled out several candidates with a sunrise in them.
 *
 * A saturated non-green object (a red helmet, cyan water) survives the wash
 * as a visible accent rather than being toned down. The activity frame has
 * one, chosen by the operator over quieter landscape options because this
 * page lists things to go and DO and a crew working a rapid says that. So
 * "no strong non-green accent" is a default, not a rule — "the copy stays
 * readable" is the rule.
 */
export const HERO_FALLBACK_IMAGE = {
  /** A range receding into a valley: depth and scale, which is what "a place
   *  you could go" looks like when the place is not yet known.
   *
   *  Also the fallback for `_tour-card.client.tsx` (2026-10-28) — a tour with
   *  no cover photo is not tied to one destination or activity, so it takes
   *  this general-purpose frame rather than a third dedicated one. Its
   *  bottom-up wash there is steeper (92% → transparent) than the listing
   *  band's, so the same "quiet lower-left, no warm patch" judgement applies
   *  with more headroom, not less. */
  destination:
    "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&w=1920&q=70",
  /** Rafters mid-rapid. Chosen by the operator (2026-10-26) over the quieter
   *  landscape options: this page lists things to GO AND DO, and a crew
   *  working a wave says that in a way an empty trail does not.
   *
   *  It is the one frame here with people and motion in it, so two things
   *  matter if it is ever swapped: keep the lower-left quiet, because the
   *  breadcrumb and title sit there, and expect the wash to mute the
   *  non-green kit (a red helmet, a race bib) rather than remove it. */
  activityType:
    "https://images.unsplash.com/photo-1530866495561-507c9faab2ed?auto=format&fit=crop&w=1920&q=70",
} as const;

/**
 * The tours index band's photo. NOT a fallback — `/tours` has no operator
 * image to fall back from, so this is the only photo that surface ever shows.
 * It lives here beside the two that are fallbacks because all three are the
 * same thing to a reader: the stock frame behind a listing band.
 */
export const TOURS_INDEX_HERO_IMAGE =
  "https://images.unsplash.com/photo-1469474968028-56623f02e42e?auto=format&fit=crop&w=1920&q=70";

/** Unsplash is a third origin on the hero's critical path, so every surface
 *  that lands on one of these warms the connection first. */
export const HERO_FALLBACK_ORIGIN = "https://images.unsplash.com";
